import * as React from 'react'

import {
  BreadcrumbDashboardDocument,
  BreadcrumbDashboardReportDocument,
  BreadcrumbDashboardTemplatesDocument,
  BreadcrumbReportDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import DashboardHeader from 'modules/reports/components/DashboardHeader'
import EditorHeader from 'modules/reports/components/EditorHeader'
import dashboards from 'modules/reports/store/collections/dashboards'
import reports from 'modules/reports/store/collections/reports'

const CloneReportPage = React.lazy(() => import('modules/reports/pages/CloneReportPage'))
const DashboardDeleteModalPage = React.lazy(
  () => import('modules/reports/pages/DashboardDeleteModalPage')
)
const DashboardEditModalPage = React.lazy(
  () => import('modules/reports/pages/DashboardEditModalPage')
)
const DashboardListPage = React.lazy(() => import('modules/reports/pages/DashboardListPage'))
const DashboardNewModalPage = React.lazy(
  () => import('modules/reports/pages/DashboardNewModalPage')
)
const DashboardRootPage = React.lazy(() => import('modules/reports/pages/DashboardRootPage'))
const DashboardShowPage = React.lazy(() => import('modules/reports/pages/DashboardShowPage'))
const EditReportPage = React.lazy(() => import('modules/reports/pages/EditReportPage'))
const EditSqlReportPage = React.lazy(() => import('modules/reports/pages/EditSqlReportPage'))
const NewQuestionModalPage = React.lazy(() => import('modules/reports/pages/NewQuestionModalPage'))
const NewReportPage = React.lazy(() => import('modules/reports/pages/NewReportPage'))
const OpenSqlReportPage = React.lazy(() => import('modules/reports/pages/OpenSqlReportPage'))
const OpenSqlTemplatePage = React.lazy(() => import('modules/reports/pages/OpenSqlTemplatePage'))
const PreviewReportPage = React.lazy(() => import('modules/reports/pages/PreviewReportPage'))
const RedirectToDashboardPage = React.lazy(
  () => import('modules/reports/pages/RedirectToDashboardPage')
)
const RootPage = React.lazy(() => import('modules/reports/pages/RootPage'))
const SqlEditorNewPage = React.lazy(() => import('modules/reports/pages/SqlEditorNewPage'))
const TemplatesPage = React.lazy(() => import('modules/reports/pages/TemplatesPage'))
const RedirectToAnalyticsReports = React.lazy(
  () => import('modules/reports/pages/RedirectToAnalyticsReportsPage')
)

const EMPTY_TITLE = ''

export const reportsRoutes = buildNavigation((navigation) => [
  buildModuleRoute({
    path: navigation.reports.path,
    handle: {
      module: 'reports',
      moduleName: () => i18n.t('sidebar.analytics'),
      meta: () => {
        return {
          title: i18n.t('custom_reports.dashboards.title'),
          subNavigation: [
            {
              title: i18n.t('sidebar.reports'),
              href: navigation.reports.dashboards.list.fullPath(),
            },
            {
              title: i18n.t('sidebar.insights'),
              href: navigation.insights.fullPath(),
            },
          ],
        }
      },
    },
    element: <RootPage />,
    children: [
      {
        index: true,
        element: <RedirectToDashboardPage />,
      },
      {
        path: navigation.reports.dashboards.path,
        children: [
          {
            path: navigation.reports.dashboards.list.path,
            loader: () => dashboards.fetch(),
            element: <DashboardListPage />,
            children: [
              {
                path: navigation.reports.dashboards.list.new.path,
                element: <DashboardNewModalPage />,
              },
            ],
          },
          {
            path: navigation.reports.dashboards.dashboardId.path,
            element: <DashboardRootPage />,
            children: [
              {
                path: navigation.reports.dashboards.dashboardId.list.path,
                element: <DashboardShowPage />,
                loader: () => Promise.all([dashboards.fetch(), reports.fetch()]),
                handle: {
                  resource: true,
                  meta: ({ watchQuery, params }) => {
                    return watchQuery({
                      query: BreadcrumbDashboardDocument,
                      fetchPolicy: 'cache-and-network',
                      variables: { id: String(params['dashboard_id']) },
                    }).map((result) => {
                      const dashboard = result.data.reports.dashboardsConnection.edges[0]?.node
                      const dashboardReports = result.data.reports.reportsConnection.edges.map(
                        ({ node }) => node
                      )

                      if (!dashboard || !dashboard.id) {
                        return { title: EMPTY_TITLE }
                      }

                      return {
                        headerComponent: (
                          <DashboardHeader dashboard={dashboard} reports={dashboardReports} />
                        ),
                        title: dashboard.title,
                      }
                    })
                  },
                },
                children: [
                  {
                    path: navigation.reports.dashboards.dashboardId.list.edit.path,
                    element: <DashboardEditModalPage />,
                  },
                  {
                    path: navigation.reports.dashboards.dashboardId.list.delete.path,
                    element: <DashboardDeleteModalPage />,
                  },
                  {
                    path: navigation.reports.dashboards.dashboardId.list.question.path,
                    element: <NewQuestionModalPage />,
                  },
                ],
              },
              {
                path: navigation.reports.dashboards.dashboardId.new.path,
                element: <NewReportPage />,
                handle: {
                  resource: true,
                  meta: ({ watchQuery, params }) => {
                    return watchQuery({
                      query: BreadcrumbDashboardDocument,
                      variables: { id: String(params['dashboard_id']) },
                    }).map((result) => {
                      const useReports = result.data.reports._policies.useReports.can
                      const dashboard_id =
                        result.data.reports.dashboardsConnection.edges[0]?.node.id

                      if (!dashboard_id) return { title: EMPTY_TITLE }

                      return {
                        headerComponent: (
                          <EditorHeader
                            canUseReports={useReports}
                            saveOnPath={navigation.reports.dashboards.dashboardId.new.fullPath({
                              dashboard_id: dashboard_id,
                            })}
                          />
                        ),
                        title: i18n.t('custom_reports.editor.new'),
                      }
                    })
                  },
                },
              },
              {
                path: navigation.reports.dashboards.dashboardId.newSharedReport.path,
                element: <NewReportPage />,
                handle: {
                  resource: true,
                  meta: ({ watchQuery, params }) => {
                    return watchQuery({
                      query: BreadcrumbDashboardReportDocument,
                      variables: {
                        dashboardId: String(params['dashboard_id']),
                        reportId: String(params['sharedReport']),
                      },
                    }).map((result) => {
                      const useReports = result.data.reports._policies.useReports.can
                      const dashboard_id =
                        result.data.reports.dashboardsConnection.edges[0]?.node.id
                      const report_id = result.data.reports.reportsConnection.edges[0]?.node.id //dashboardsConnection.edges[0]?.node.id

                      if (!dashboard_id || !report_id) return { title: EMPTY_TITLE }

                      return {
                        headerComponent: (
                          <EditorHeader
                            canUseReports={useReports}
                            saveOnPath={navigation.reports.dashboards.dashboardId.newSharedReport.fullPath(
                              {
                                dashboard_id: dashboard_id,
                                sharedReport: report_id,
                              }
                            )}
                          />
                        ),
                        title: i18n.t('custom_reports.editor.new'),
                      }
                    })
                  },
                },
              },
              {
                path: navigation.reports.dashboards.dashboardId.templates.path,
                element: <TemplatesPage />,
                handle: {
                  meta: () => ({
                    title: i18n.t('custom_reports.templates.title'),
                  }),
                },
              },
              {
                path: navigation.reports.dashboards.dashboardId.templatesTemplateSlug.path,
                element: <PreviewReportPage />,
                handle: {
                  resource: true,
                  meta: ({ watchQuery, params }) => {
                    return watchQuery({
                      query: BreadcrumbDashboardTemplatesDocument,
                      variables: { id: String(params['dashboard_id']) },
                    }).map((result) => {
                      const useReports = result.data.reports._policies.useReports.can
                      const dashboard_id =
                        result.data.reports.dashboardsConnection.edges[0]?.node.id
                      const template_slug = result.data.reports.templatesConnection.edges
                        .map(({ node: { slug } }) => slug)
                        .find((slug) => slug == params['template_slug'])

                      if (!dashboard_id || !template_slug) return { title: EMPTY_TITLE }

                      return {
                        headerComponent: (
                          <EditorHeader
                            canUseReports={useReports}
                            saveOnPath={navigation.reports.dashboards.dashboardId.templatesTemplateSlug.fullPath(
                              {
                                template_slug: template_slug,
                                dashboard_id: dashboard_id,
                              }
                            )}
                          />
                        ),
                        title: i18n.t('custom_reports.editor.new'),
                      }
                    })
                  },
                },
              },
              {
                path: navigation.reports.dashboards.dashboardId.templatesTemplateSlugSql.path,
                element: <OpenSqlTemplatePage />,
                handle: {
                  resource: true,
                  meta: ({ watchQuery, params }) => {
                    return watchQuery({ query: BreadcrumbDashboardTemplatesDocument }).map(
                      (result) => {
                        const useReports = result.data.reports._policies.useReports.can
                        const dashboard_id =
                          result.data.reports.dashboardsConnection.edges[0]?.node.id
                        const template_slug = result.data.reports.templatesConnection.edges
                          .map(({ node: { slug } }) => slug)
                          .find((slug) => slug == params['template_slug'])

                        if (!dashboard_id || !template_slug) return { title: EMPTY_TITLE }

                        return {
                          headerComponent: (
                            <EditorHeader
                              canUseReports={useReports}
                              saveOnPath={navigation.reports.dashboards.dashboardId.templatesTemplateSlugSql.fullPath(
                                {
                                  template_slug: template_slug,
                                  dashboard_id: dashboard_id,
                                }
                              )}
                            />
                          ),
                          title: i18n.t('custom_reports.editor.new'),
                        }
                      }
                    )
                  },
                },
              },
              {
                path: navigation.reports.dashboards.dashboardId.sql.path,
                element: <SqlEditorNewPage />,
                handle: {
                  resource: true,
                  meta: ({ watchQuery, params }) => {
                    return watchQuery({
                      query: BreadcrumbDashboardDocument,
                      variables: { id: String(params['dashboard_id']) },
                    }).map((result) => {
                      const useReports = result.data.reports._policies.useReports.can
                      const dashboard_id =
                        result.data.reports.dashboardsConnection.edges[0]?.node.id

                      if (!dashboard_id) return { title: EMPTY_TITLE }

                      return {
                        headerComponent: (
                          <EditorHeader
                            canUseReports={useReports}
                            saveOnPath={navigation.reports.dashboards.dashboardId.sql.fullPath({
                              dashboard_id: dashboard_id,
                            })}
                          />
                        ),
                        title: i18n.t('custom_reports.editor.new'),
                      }
                    })
                  },
                },
              },
            ],
          },
        ],
      },
      {
        path: navigation.reports.id.path,
        children: [
          {
            index: true,
            element: <EditReportPage />,
            handle: {
              resource: true,
              meta: ({ params, watchQuery }) => {
                return watchQuery({
                  query: BreadcrumbReportDocument,
                  variables: { id: String(params['id']) },
                }).map((result) => {
                  const useReports = result.data.reports._policies.useReports.can
                  const report = result.data.reports.reportsConnection.edges[0]?.node

                  if (!report) return { title: EMPTY_TITLE }

                  return {
                    headerComponent: (
                      <EditorHeader
                        export
                        reportId={report.id}
                        canUseReports={useReports}
                        saveOnPath={navigation.reports.id.fullPath({
                          id: report.id,
                        })}
                      />
                    ),
                    title: report.title,
                  }
                })
              },
            },
          },
          {
            path: navigation.reports.id.edit.path,
            element: <EditReportPage />,
            handle: {
              resource: true,
              meta: ({ params, watchQuery }) => {
                return watchQuery({
                  query: BreadcrumbReportDocument,
                  variables: { id: String(params['id']) },
                }).map((result) => {
                  const useReports = result.data.reports._policies.useReports.can
                  const report = result.data.reports.reportsConnection.edges[0]?.node

                  if (!report) return { title: EMPTY_TITLE }

                  return {
                    headerComponent: (
                      <EditorHeader
                        export
                        reportId={report.id}
                        canUseReports={useReports}
                        saveOnPath={navigation.reports.id.edit.fullPath({
                          id: report.id,
                        })}
                      />
                    ),
                    title: report.title,
                  }
                })
              },
            },
          },
          {
            path: navigation.reports.id.sqlEditor.path,
            element: <EditSqlReportPage />,
            handle: {
              resource: true,
              meta: ({ params, watchQuery }) => {
                return watchQuery({
                  query: BreadcrumbReportDocument,
                  variables: { id: String(params['id']) },
                }).map((result) => {
                  const useReports = result.data.reports._policies.useReports.can
                  const report = result.data.reports.reportsConnection.edges[0]?.node

                  if (!report) return { title: EMPTY_TITLE }

                  return {
                    headerComponent: (
                      <EditorHeader
                        export
                        reportId={report.id}
                        canUseReports={useReports}
                        saveOnPath={navigation.reports.id.sqlEditor.fullPath({
                          id: report.id,
                        })}
                      />
                    ),
                    title: report.title,
                  }
                })
              },
            },
          },
          {
            path: navigation.reports.id.openSql.path,
            element: <OpenSqlReportPage />,
            handle: {
              resource: true,
              meta: ({ params, watchQuery }) => {
                return watchQuery({
                  query: BreadcrumbReportDocument,
                  variables: { id: String(params['id']) },
                }).map((result) => {
                  const useReports = result.data.reports._policies.useReports.can
                  const report = result.data.reports.reportsConnection.edges[0]?.node

                  if (!report) return { title: EMPTY_TITLE }

                  return {
                    headerComponent: (
                      <EditorHeader
                        export
                        reportId={report.id}
                        canUseReports={useReports}
                        saveOnPath={navigation.reports.id.openSql.fullPath({
                          id: report.id,
                        })}
                      />
                    ),
                    title: report.title,
                  }
                })
              },
            },
          },
          {
            path: navigation.reports.id.clone.path,
            element: <CloneReportPage />,
            handle: {
              resource: true,
              meta: ({ params, watchQuery }) => {
                return watchQuery({
                  query: BreadcrumbReportDocument,
                  variables: { id: String(params['id']) },
                }).map((result) => {
                  const useReports = result.data.reports._policies.useReports.can
                  const report = result.data.reports.reportsConnection.edges[0]?.node

                  if (!report) return { title: EMPTY_TITLE }

                  return {
                    headerComponent: (
                      <EditorHeader
                        export
                        reportId={report.id}
                        canUseReports={useReports}
                        saveOnPath={navigation.reports.id.clone.fullPath({
                          id: report.id,
                        })}
                      />
                    ),
                    title: i18n.t('custom_reports.editor.new'),
                  }
                })
              },
            },
          },
        ],
      },
    ],
  }),
  {
    // legacy reports route
    path: 'reports',
    element: <RedirectToAnalyticsReports />,
  },
])
